import { useEffect, useMemo, useState } from "react";

import ApiService from "../../../../services/ApiService";

// Fallback list used only when the campaign's real CampaignMemberStatus values
// cannot be loaded from Salesforce. The authoritative, per-campaign list is
// fetched at runtime so both Events team (Unsent/Invited) and BD team
// (To be sent/Sent) campaigns show the correct options.
export const DEFAULT_STATUS_OPTIONS = [
  "Unsent",
  "To be sent",
  "Invited",
  "Declined",
  "Politely Declined",
  "Attending",
  "Tentative",
  "Regrets",
  "Declined, Booked TAP Call",
  "Gave referral",
  "Remove/Unsubscribe from Brand",
  "Remove/Unsubscribe From All",
  "Hold",
];

export const CAMPAIGN_TYPE_STATUS_MAP = {
  "BD Client Marketing": "To be sent",
  "Client Acquisition Marketing": "To be sent",
  "Client Portfolio Marketing": "To be sent",
  "Curate Attendees": "Unsent",
  "Event Sponsor Outreach": "Unsent",
  "Landmark Event Attendee Marketing": "Unsent",
  "Learning Journey Delegate Recruitment": "Unsent",
  "Summit Event Attendee Marketing": "Unsent",
  "TAP Marketing": "To be sent",
  "Virtual Event Outreach": "Unsent",
};

export function getRecommendedStatusForCampaignType(campaignType) {
  return CAMPAIGN_TYPE_STATUS_MAP[campaignType] || "Unsent";
}

// Loads the valid CampaignMemberStatus options for a Salesforce campaign.
// Returns the status labels (sorted by default then Salesforce sort order),
// the campaign's default ("not yet sent") status, and a loading flag. Falls
// back to the static list / record-type recommendation when the fetch fails.
export default function useCampaignMemberStatuses({
  employee,
  salesforceCampaignId,
  recordTypeName,
} = {}) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizedCampaignId = String(salesforceCampaignId || "").trim();

  useEffect(() => {
    if (!employee || !normalizedCampaignId) {
      setStatuses([]);
      return undefined;
    }
    let cancelled = false;
    const loadStatuses = async () => {
      setLoading(true);
      try {
        const api = new ApiService({ employee });
        const response = await api.get(
          `landmark/salesforce/getCampaignMemberStatuses/${normalizedCampaignId}`
        );
        if (cancelled) return;
        const options = (Array.isArray(response) ? response : [])
          .map((status) => {
            const label = String(status?.label || status?.value || "").trim();
            if (!label) return null;
            return {
              label,
              value: label,
              isDefault: !!status?.isDefault,
              hasResponded: !!status?.hasResponded,
              sortOrder: Number(status?.sortOrder || 0),
            };
          })
          .filter(Boolean);
        options.sort((a, b) => {
          if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
          const sortOrderDiff = Number(a.sortOrder || 0) - Number(b.sortOrder || 0);
          if (sortOrderDiff !== 0) return sortOrderDiff;
          return String(a.label || "").localeCompare(String(b.label || ""));
        });
        setStatuses(options);
      } catch (_error) {
        if (!cancelled) setStatuses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadStatuses();
    return () => {
      cancelled = true;
    };
  }, [employee, normalizedCampaignId]);

  const statusOptions = useMemo(
    () => (statuses.length ? statuses.map((status) => status.label) : DEFAULT_STATUS_OPTIONS),
    [statuses]
  );

  const defaultStatus = useMemo(() => {
    const apiDefault = statuses.find((status) => status.isDefault);
    if (apiDefault) return apiDefault.label;
    return getRecommendedStatusForCampaignType(recordTypeName);
  }, [statuses, recordTypeName]);

  return { statusOptions, defaultStatus, statuses, loading };
}
